import type {
  InvoiceStatus,
  InvoiceType,
  InvoiceLine,
  VatCategory,
  VatGroup,
} from "../../types/invoice";

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const VAT_RATE: Record<VatCategory, number> = { "24": 24, "0": 0, NO_TAX: 0 };

// Mirrors backend Invoice.computeTotals: groups by VAT category (0/24/NO_TAX
// kept distinct), NO_TAX carries no VAT. Order: 24, 0, NO_TAX.
export function computeTotals(lines: InvoiceLine[]): {
  vatGroups: VatGroup[];
  subtotal: number;
  vatAmount: number;
  total: number;
} {
  const groups = new Map<VatCategory, { subtotal: number; vatAmount: number }>();
  for (const l of lines) {
    const cat = (l.vatCategory || "NO_TAX") as VatCategory;
    const qty = Number(l.quantity) || 0;
    const price = Number(l.unitPrice) || 0;
    const lineTotal =
      l.lineTotal != null ? Number(l.lineTotal) : round2(qty * price);
    const g = groups.get(cat) || { subtotal: 0, vatAmount: 0 };
    g.subtotal += lineTotal;
    g.vatAmount += lineTotal * (VAT_RATE[cat] / 100);
    groups.set(cat, g);
  }
  const order: VatCategory[] = ["24", "0", "NO_TAX"];
  const vatGroups = order
    .filter((c) => groups.has(c))
    .map((c) => ({
      vatCategory: c,
      subtotal: round2(groups.get(c)!.subtotal),
      vatAmount: round2(groups.get(c)!.vatAmount),
    }));
  const subtotal = round2(vatGroups.reduce((s, g) => s + g.subtotal, 0));
  const vatAmount = round2(vatGroups.reduce((s, g) => s + g.vatAmount, 0));
  return { vatGroups, subtotal, vatAmount, total: round2(subtotal + vatAmount) };
}

/**
 * The audit references for one billed container.
 *
 * One entry per container, not per line: a box charged for four services is
 * still one physical container and one booking, and repeating its paperwork
 * four times buries the thing being reconciled. Containers whose lines carry no
 * outside reference at all are dropped — our own line number is already on the
 * line and adds nothing as a "reference".
 *
 * Shared by the preview, the detail page and (in its own implementation)
 * `invoicePdfService`, so all three list the same containers.
 */
export interface ContainerAuditRef {
  key: string;
  lineNumber: string;
  containerNumber?: string;
  orderNumber?: string;
  orderReference?: string;
  bookingRef?: string;
}

export function containerAuditRefs(lines: InvoiceLine[]): ContainerAuditRef[] {
  const seen = new Set<string>();
  const out: ContainerAuditRef[] = [];
  for (const l of lines) {
    if (!l.containerLineId && !l.containerLineNumber) continue;
    const key = String(l.containerLineId || l.containerLineNumber);
    if (seen.has(key)) continue;
    seen.add(key);
    if (
      !l.containerNumber &&
      !l.containerOrderNumber &&
      !l.containerOrderReference &&
      !l.containerBookingRef
    )
      continue;
    out.push({
      key,
      lineNumber: l.containerLineNumber || "",
      containerNumber: l.containerNumber,
      orderNumber: l.containerOrderNumber,
      orderReference: l.containerOrderReference,
      bookingRef: l.containerBookingRef,
    });
  }
  return out;
}

export const money = (n: number | undefined | null) => {
  const v = Number(n) || 0;
  const s = Math.abs(v)
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${v < 0 ? "-" : ""}€${s}`;
};

export const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

export const STATUS_BADGE: Record<InvoiceStatus, string> = {
  draft: "bg-[#F1EFE8] text-[#444441]",
  sent: "bg-[#E6F1FB] text-[#0C447C]",
  paid: "bg-[#EAF3DE] text-[#27500A]",
  overdue: "bg-[#FCEBEB] text-[#791F1F]",
  cancelled: "bg-[#F1EFE8] text-[#9b9b96]",
};

export const statusLabel = (
  status: InvoiceStatus,
  type: InvoiceType,
): string => {
  if (type === "credit_note") return "Credit note";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export const isOverdue = (i: {
  status: InvoiceStatus;
  dueDate: string;
  invoiceType: InvoiceType;
}) =>
  i.invoiceType === "invoice" &&
  i.status === "sent" &&
  !!i.dueDate &&
  new Date(i.dueDate) < new Date();
